import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Shield, Globe, Users, Truck, CheckCircle2, Anchor } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1724525231969-173f10849bb1?crop=entropy&cs=srgb&fm=webp&w=800&q=75&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjBzaGlwcGluZyUyMGNhcmdvJTIwY29udGFpbmVyJTIwc2hpcCUyMGFpcnBsYW5lJTIwdHJ1Y2slMjB3YXJlaG91c2V8ZW58MHx8fHwxNzcyNDQ4MjUyfDA&ixlib=rb-4.1.0";

const strengths = [
  { icon: Globe, title: "Global Network", desc: "Strategic partnerships with international carriers and agents across major trade routes." },
  { icon: Shield, title: "Licensed Operations", desc: "Fully licensed customs brokerage with certified professionals handling your documentation." },
  { icon: Users, title: "Expert Team", desc: "Dedicated logistics professionals with deep industry knowledge and experience." },
  { icon: Truck, title: "Own Fleet", desc: "Well-maintained fleet of vehicles for reliable inland transportation services." },
  { icon: Anchor, title: "Port Operations", desc: "Strong presence at major Indian ports for efficient cargo handling and clearance." },
  { icon: CheckCircle2, title: "Quality Assurance", desc: "Rigorous quality processes to ensure safe and timely delivery of every shipment." },
];

export default function AboutPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="page-header py-20 md:py-28" data-testid="about-page-header">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-4" data-testid="about-heading">
            About Pranav Logistics
          </h1>
          <p className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto">
            Your trusted logistics partner with 18+ years of excellence in cargo solutions
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 md:py-24 bg-white" data-testid="who-we-are-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Who We Are</span>
              <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-6">
                A Legacy of Reliable Logistics
              </h2>
              <div className="section-divider mb-6" />
              <p className="text-slate-600 leading-relaxed mb-4">
                Established over 18 years ago, Pranav Logistics has grown from a local freight forwarding company to one of the most trusted logistics service providers in Gujarat. Based in Rajkot, we serve clients across India and internationally.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our comprehensive range of services includes inland transportation, customs clearance, ocean freight, air freight, warehousing, and specialized project cargo handling. We pride ourselves on delivering personalized service that caters to the unique requirements of each client.
              </p>
              <p className="text-slate-600 leading-relaxed">
                With a dedicated team of logistics professionals, licensed customs brokers, and a network that spans major ports and airports across India, we ensure your cargo moves seamlessly from origin to destination.
              </p>
            </div>
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Logistics operations at port"
                className="rounded-lg shadow-lg w-full h-[400px] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F97316] text-white rounded-lg p-6 shadow-xl hidden md:block">
                <div className="font-['Barlow_Condensed'] font-extrabold text-4xl">18+</div>
                <div className="text-sm font-medium">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Our Purpose</span>
            <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-4">
              Mission & Vision
            </h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 md:p-10 border border-slate-200 shadow-sm" data-testid="mission-card">
              <div className="bg-[#FFF7ED] p-4 rounded-lg w-fit mb-6">
                <Target size={32} strokeWidth={1.5} className="text-[#F97316]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] font-bold text-2xl text-[#0F172A] mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To provide reliable, efficient, and cost-effective logistics solutions that empower businesses to grow and compete in the global marketplace. We are committed to delivering exceptional service through innovation, expertise, and unwavering dedication to our clients' success.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 md:p-10 border border-slate-200 shadow-sm" data-testid="vision-card">
              <div className="bg-[#EFF6FF] p-4 rounded-lg w-fit mb-6">
                <Eye size={32} strokeWidth={1.5} className="text-[#0EA5E9]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] font-bold text-2xl text-[#0F172A] mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To be the most trusted and preferred logistics partner in India, recognized for our operational excellence, technological innovation, and commitment to creating value for our clients, employees, and stakeholders at every touchpoint.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Strength */}
      <section className="py-16 md:py-24 bg-white" data-testid="operational-strength-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-[#F97316] font-semibold text-sm uppercase tracking-widest">Our Capabilities</span>
            <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-3 mb-4">
              Operational Strength
            </h2>
            <div className="section-divider mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {strengths.map((s, i) => (
              <div key={i} className="service-card group bg-white border border-slate-200 hover:border-[#0EA5E9] rounded-lg p-6" data-testid={`strength-card-${i}`}>
                <div className="bg-[#F1F5F9] rounded-lg p-4 w-fit mb-5 group-hover:bg-[#FFF7ED] transition-colors duration-300">
                  <s.icon size={28} strokeWidth={1.5} className="service-icon text-[#0EA5E9]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-xl text-[#0F172A] mb-2">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#0F172A] to-[#1E293B]" data-testid="about-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl text-white mb-4">
            Ready to Ship With Confidence?
          </h2>
          <p className="text-slate-300 text-base max-w-xl mx-auto mb-8">
            Let us handle your logistics needs while you focus on growing your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services" className="btn-primary inline-flex items-center gap-2" data-testid="about-cta-services-btn">
              Our Services <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#0F172A] inline-flex items-center gap-2" data-testid="about-cta-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
